import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { GAME_NAME } from "@/config/game";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import {
  PRICE_SOURCE_LABEL_KEYS,
  getObjectiveDisplayInfo,
  getPriceDisplay,
} from "@/functions/getPriceDisplay";
import { MetaRatingIcon } from "@/icons";
import { cn } from "@/lib/utils";
import {
  contentLabelsSelector,
  eventTokensSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import {
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "@/redux/players/playersSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { ObjectiveDisplay, PriceInfo } from "@repo/shared/types";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

interface CurrentStateProps {
  metaRating: number;
  metaRatingZeroChem: number;
  chemStyleId: number;
  playerRoleId: number;
  rolesPlus: number[];
  price: Price;
  itemScore?: number;
}

const SectionElement = ({
  children,
  label,
  align = "center",
  inline = false,
}: {
  children: React.ReactNode;
  label: string;
  /** The grid reads as columns when its cells share a left edge. */
  align?: "center" | "start";
  /** From touch-device up, value and label share a line (the price list). */
  inline?: boolean;
}) => (
  <div
    className={cn(
      "w-full h-full flex flex-col gap-y-0.5 mobile:gap-y-2 justify-center",
      align === "start" ? "items-start" : "items-center",
      inline &&
        "touch-device:flex-row touch-device:items-center touch-device:justify-between touch-device:gap-x-4",
    )}
  >
    {children}
    {/* Empty label = the value speaks for itself (the role and chemstyle chips). */}
    {label && (
      <p className="text-[11px] mobile:text-xs text-dark-tertiary whitespace-nowrap">
        {label}
      </p>
    )}
  </div>
);

interface Price {
  // Absent for players with no price concept (evolved/builder cards)
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
}

export const CurrentState = ({
  metaRating,
  metaRatingZeroChem,
  chemStyleId,
  playerRoleId,
  rolesPlus,
  price,
  itemScore,
}: CurrentStateProps) => {
  const rolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus,
  });
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isSmallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const { chemstyles } = useSelector(contentLabelsSelector);
  const { t } = useTranslation(["players", "common"]);
  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({
      playerRoleId: playerRoleId,
      shortName: isSmallDesktop,
    }),
  );
  const tier = useSelector(tierSelector(metaRatingZeroChem, playerRoleId));
  const eventTokens = useSelector(eventTokensSelector);
  // The grid wants a shared left edge; the desktop row stays centred.
  const align = "center";

  const PriceSectionElement = ({ inline }: { inline?: boolean }) => {
    if (price.priceInfo?.source === "objective") {
      const { iconUrl, text, truncate } = getObjectiveDisplayInfo(
        price.objectiveDisplay,
      );

      return (
        <SectionElement
          label={t(PRICE_SOURCE_LABEL_KEYS.objective)}
          align={align}
          inline={inline}
        >
          <div className="text-sm text-dark-primary flex flex-row items-center gap-1">
            <img className="w-5 h-5 shrink-0" src={iconUrl} />
            {text && (
              <p
                className={cn(
                  "text-sm text-dark-primary",
                  truncate && "truncate max-w-24 mobile:max-w-32",
                )}
              >
                {text}
              </p>
            )}
          </div>
        </SectionElement>
      );
    }

    const { iconUrl, label, showNumber, displayPrice, tokenName } =
      getPriceDisplay(price.priceInfo, eventTokens);
    const priceText = isMobile
      ? convertPlayerPrice(displayPrice, true)
      : displayPrice?.toLocaleString("en-US");

    return (
      <SectionElement
        label={
          tokenName ??
          t(PRICE_SOURCE_LABEL_KEYS[price.priceInfo?.source ?? "market"])
        }
        align={align}
        inline={inline}
      >
        <div className="text-sm text-dark-primary flex flex-row items-center gap-1 mt-4 mobile:mt-0">
          {iconUrl && (
            <img
              className={cn(
                "w-4 h-4",
                price.priceInfo?.source === "sbc" && "w-4.5 h-4.5",
              )}
              src={iconUrl}
            />
          )}
          {/* A token's name is already the section label. */}
          {label && !tokenName && (
            <p className="text-sm text-dark-primary">{label}</p>
          )}
          {showNumber && (
            <p className="font-oswald text-sm font-medium text-dark-primary">
              {priceText}
            </p>
          )}
        </div>
      </SectionElement>
    );
  };

  const hasLaunchPrice =
    price.priceInfo?.source === "market" &&
    getPriceDisplay(price.priceInfo).launchPrice > 0;

  const LaunchPriceSectionElement = ({ inline }: { inline?: boolean }) => {
    const { iconUrl, launchPrice } = getPriceDisplay(price.priceInfo);
    /*
      Only market cards have a launch price to compare against. The empty cell
      still occupies its column, so the chips below stay in the third row
      instead of sliding up into the gap.
    */
    if (price.priceInfo?.source !== "market" || launchPrice <= 0)
      return <div aria-hidden />;

    return (
      <SectionElement
        label={t("detailed.currentState.launchPrice", { edition: GAME_NAME })}
        align={align}
        inline={inline}
      >
        <div className="text-sm text-dark-primary flex flex-row items-center gap-1 mt-4 mobile:mt-0">
          <img className="w-4 h-4" src={iconUrl} />
          <p className="font-oswald text-sm font-medium text-dark-primary">
            {convertPlayerPrice(launchPrice, isMobile)}
          </p>
        </div>
      </SectionElement>
    );
  };

  const ItemScoreSectionElement = ({ inline }: { inline?: boolean }) => (
    <SectionElement
      label={t("detailed.currentState.itemScore")}
      align={align}
      inline={inline}
    >
      <div className="text-sm text-dark-primary flex flex-row items-center gap-1 mt-4 mobile:mt-0">
        <ItemScoreDiamond size={16} />
        <p className="font-oswald text-sm font-medium text-dark-primary">
          {itemScore?.toLocaleString("en-US")}
        </p>
      </div>
    </SectionElement>
  );
  const hasItemScore = itemScore !== undefined;

  const metaRatingCell = (
    <SectionElement
      label={isMobile ? "" : t("common:metaRating")}
      align={align}
    >
      <div className="flex flex-row items-center gap-2 text-dark-tertiary">
        <p className="text-base mobile:text-xl font-bold text-dark-primary">
          {metaRating?.toFixed(1)}
        </p>
        <MetaRatingIcon
          width={isMobile ? "16" : "20"}
          height={isMobile ? "16" : "20"}
        />
      </div>
    </SectionElement>
  );

  const tierCell = (
    <SectionElement
      label={isMobile ? "" : t("detailed.currentState.metaTier")}
      align={align}
    >
      <p
        className={cn(
          "text-base mobile:text-lg font-bold",
          tier?.tierTextColor,
          !playerRoleId && "invisible",
        )}
      >
        {tier?.tierName || "-"}
      </p>
    </SectionElement>
  );

  const metaRatingAndTierCell = (
    <div className="flex flex-row items-center justify-center gap-2 touch-device:gap-8 w-35 touch-device:w-40 large-desktop:w-50 shrink-0">
      {metaRatingCell}
      {tierCell}
    </div>
  );

  const roleCell = (
    <SectionElement
      label={isMobile ? "" : t("detailed.currentState.playerRole")}
      align={align}
    >
      <div
        className={cn(
          "flex flex-row items-center gap-1 mobile:gap-2 bg-white/5 rounded-sm mobile:rounded-full px-1 mobile:pl-2 mobile:pr-3 py-1 h-6 mobile:h-8",
          !playerRoleId && "invisible",
        )}
      >
        <p className="text-xs font-medium mobile:text-sm text-dark-secondary truncate max-small-mobile:max-w-24 max-mobile:max-w-28">
          {playerRoleLabel}
        </p>
        {rolesPlusOrPlusPlus && (
          <PlusPlus
            plus={rolesPlusOrPlusPlus === "plus"}
            plusPlus={rolesPlusOrPlusPlus === "plusPlus"}
          />
        )}
      </div>
    </SectionElement>
  );

  const chemStyleCell = (
    <SectionElement
      label={isMobile ? "" : t("detailed.currentState.chemstyle")}
      align={align}
    >
      <div
        className={cn(
          "flex flex-row items-center gap-2  bg-white/5 rounded-sm mobile:rounded-full pl-2 pr-3 py-1 h-6 mobile:h-8",
          !chemStyleId && "invisible",
        )}
      >
        <div
          className="mobile:text-xl text-dark-primary w-4 mobile:w-6"
          style={{
            fontFamily: "UltimateTeamIcons",
          }}
        >
          {chemStylesToIcon[chemStyleId]}
        </div>
        <p className="text-xs mobile:text-sm text-dark-secondary -ml-1.5">
          {chemstyles[chemStyleId]?.name}
        </p>
      </div>
    </SectionElement>
  );

  /*
    Phones get the stats as a 2x3 grid filling the space beside the card, with
    no panel behind them: the card art is the backdrop, and a blurred plate on
    top of it only shrank the room the numbers had. Each value carries its own
    label, which the single-row desktop layout can leave to a header.
  */
  if (isMobile)
    return (
      <div className="w-full grid grid-cols-2 gap-x-3 gap-y-1 text-white ml-1">
        {metaRatingCell}
        {tierCell}
        {roleCell}
        {chemStyleCell}
        <PriceSectionElement />
        {/* Item score takes the launch price's slot when there is none. */}
        {(hasLaunchPrice || !hasItemScore) && <LaunchPriceSectionElement />}
        {hasItemScore && <ItemScoreSectionElement />}
      </div>
    );

  /*
    Two plates. Side by side from touch-device up, the price plate listing one
    figure per line; below that the row has no room, so the price plate sits
    under the stats and lays its figures out like them.
  */
  return (
    <div className="py-4 my-auto px-4 w-full flex flex-col items-start gap-2 touch-device:flex-row touch-device:items-center touch-device:gap-x-8">
      <div className="flex shrink-0 flex-row gap-2 touch-device:gap-8 backdrop-blur-md shadow-soft-dark-bottom bg-panel-background px-4 py-3 rounded-2xl">
        {metaRatingAndTierCell}
        <div className="flex flex-row gap-x-2">
          {roleCell}
          {chemStyleCell}
        </div>
      </div>
      <div className="flex shrink-0 flex-row gap-x-6 touch-device:flex-col touch-device:justify-center touch-device:gap-y-2 px-4 py-3 rounded-2xl backdrop-blur-md shadow-soft-dark-bottom bg-panel-background">
        <PriceSectionElement inline />
        {hasLaunchPrice && <LaunchPriceSectionElement inline />}
        {hasItemScore && <ItemScoreSectionElement inline />}
      </div>
    </div>
  );
};
